import React from 'react';
import { RiskLevel } from '../components/ui/Classification/Classification.types';
import { startOnboarding } from '../services/OnboardingService';
import { GuideSource } from '../types/OnboardingGuide.types';
import { SystemLoggingEvent } from '../types/SystemLogging.types';
import { AppDispatch } from '../store';
import {
  openTutorialGuide,
  setTutorialGuideSessionId
} from '../store/slices/tutorialGuideSlice';
import { NavigateFunction } from 'react-router-dom';
import { PATHS } from '../constants/Paths.constants';
import { fetchDashboardStart } from '../store/slices/dashboardSlice';
import { PlatformRole } from '../types/Team.types';

export const mapClassificationToRiskLevel = (
  classification: string
): RiskLevel => {
  switch (classification.toLowerCase()) {
    case 'high_risk_exception':
    case 'high_risk':
    case 'high-risk':
      return 'high';
    case 'limited_risk_exception':
    case 'limited_risk':
    case 'limited-risk':
      return 'limited';
    case 'prohibited':
      return 'prohibited';
    case 'minimal_risk':
    case 'minimal-risk':
      return 'minimal';
    case 'excluded':
    case 'excluded_exception':
      return 'excluded';
    case 'out_of_scope':
    case 'out-of-scope':
      return 'out-of-scope';
    default:
      return 'minimal'; // fallback za nepoznate tipove
  }
};

export const formatClassificationText = (text: string): string => {
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('-');
};

export function formatSourceElement(html: string, underlineClass: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('a').forEach((a) => {
    a.classList.add(underlineClass);
  });

  return doc.body.innerHTML;
}

export const getBreakClass = (text: string): string => {
  return text?.trim().includes(' ') ? 'break-words' : 'break-all';
};

export const handleInputChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  setName: React.Dispatch<React.SetStateAction<string>>
) => {
  const value = e.target.value;
  if (/[^a-zA-ZÀ-ÖØ-öø-ÿĀ-ſ\s'-]/.test(value)) return; // Only allow letters (incl. Latin diacritics), spaces, apostrophes, and hyphens

  setName(value);
};

export const startTutorialGuideFlow = async (
  dispatch: AppDispatch,
  source: GuideSource,
  navigate?: NavigateFunction,
  location?: string
) => {
  const response = await startOnboarding(source);

  if (location !== PATHS.DASHBOARD && navigate) {
    dispatch(fetchDashboardStart());
    navigate(PATHS.DASHBOARD);
  }

  dispatch(setTutorialGuideSessionId(response.id));
  dispatch(openTutorialGuide('WELCOME'));

  return response;
};

export const getEventUserDisplay = (event: SystemLoggingEvent) => {
  if (event.user) {
    const firstName = event.user.firstName ?? '';
    const lastName = event.user.lastName ?? '';
    const name =
      `${firstName} ${lastName}`.trim() || event.user.email || 'Unknown user';
    // Uppercased to match every other avatar in the app, which this branch
    // previously did not do — audit-log rows rendered lowercase initials.
    const initials =
      `${firstName.charAt(0)}${lastName.charAt(0)}`.trim().toUpperCase() ||
      (event.user.email?.slice(0, 2).toUpperCase() ?? 'NA');

    return { name, initials };
  }

  const githubName = event.githubUserName?.trim();
  const fallbackName =
    githubName && githubName.length > 0 ? githubName : 'Unknown user';
  const initials = fallbackName.slice(0, 2).toUpperCase() || 'NA';

  return { name: fallbackName, initials };
};

const PLATFORM_ROLE_LABELS: Record<PlatformRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  USER: 'User'
};

export const getPlatformRoleLabel = (role?: PlatformRole | null) =>
  (role && PLATFORM_ROLE_LABELS[role]) || '';
